import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatCurrency, formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Payroll({ user, onLogout }) {
  const [payroll, setPayroll] = useState([]);
  const [employees, setEmployees] = useState({});
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    fetchPayroll();
    fetchEmployees();
    fetchDashboardStats();
  }, [month, year]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const empMap = {};
      response.data.forEach(emp => {
        empMap[emp.id] = emp;
      });
      setEmployees(empMap);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchPayroll = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayroll(response.data);
    } catch (error) {
      console.error('Failed to fetch payroll:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/payroll/generate?month=${month}&year=${year}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPayroll();
      alert('Payroll generated successfully!');
    } catch (error) {
      alert('Failed to generate payroll: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (payrollId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/payroll/${payrollId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPayroll();
      alert('Payroll approved successfully!');
    } catch (error) {
      alert('Failed to approve payroll: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getEmployeeName = (empId) => {
    return employees[empId] ? `${employees[empId].first_name} ${employees[empId].last_name}` : empId;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Payroll Management</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button 
              onClick={handleGenerate} 
              disabled={loading} 
              className="px-6 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50" 
              style={{backgroundColor: '#2b3380'}}
            >
              {loading ? 'Generating...' : 'Generate Payroll'}
            </button>
          )}
        </div>

        {/* Payroll Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Payroll Overview"
            icon="💰"
            bgColor="#9c27b0"
            stats={[
              {
                label: 'Pending Payroll',
                value: dashboardStats.pending_payroll || 0,
                subtitle: 'To be processed',
                color: '#9c27b0'
              },
              {
                label: 'Total Amount',
                value: `₹${((Number(dashboardStats.total_payroll_amount) || 0) / 100000).toFixed(1)}L`,
                subtitle: 'Current month',
                color: '#673ab7'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Active staff',
                color: '#2b3380'
              },
              {
                label: 'Processing Rate',
                value: dashboardStats.total_employees > 0 ? '100%' : '0%',
                subtitle: dashboardStats.total_employees > 0 ? 'On time' : 'No payroll yet',
                color: '#4caf50'
              }
            ]}
          />
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex gap-4 items-center">
            <label className="font-semibold text-gray-700">Select Period:</label>
            <select 
              value={month} 
              onChange={(e) => setMonth(parseInt(e.target.value))} 
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b3380]"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i+1} value={i+1}>
                  {new Date(2024, i).toLocaleString('default', {month: 'long'})}
                </option>
              ))}
            </select>
            <select 
              value={year} 
              onChange={(e) => setYear(parseInt(e.target.value))} 
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b3380]"
            >
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {payroll.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">No payroll records found for this period.</p>
            {(user.role === 'Admin' || user.role === 'HR Manager') && (
              <p className="text-gray-400 mt-2">Click "Generate Payroll" to create records.</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                  <tr>
                    <th className="px-4 py-3 text-left">Employee</th>
                    <th className="px-4 py-3 text-left">Days</th>
                    <th className="px-4 py-3 text-left">Basic Salary</th>
                    <th className="px-4 py-3 text-left">Gross Salary</th>
                    <th className="px-4 py-3 text-left">Deductions</th>
                    <th className="px-4 py-3 text-left">Net Salary</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.map(p => (
                    <tr key={p.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{getEmployeeName(p.employee_id)}</div>
                        <div className="text-sm text-gray-500">{p.employee_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <span className="font-medium">{p.present_days}</span>
                          <span className="text-gray-400">/{p.working_days}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{formatCurrency(p.basic_salary)}</td>
                      <td className="px-4 py-3">{formatCurrency(p.gross_salary)}</td>
                      <td className="px-4 py-3 text-red-600">{formatCurrency(p.total_deductions)}</td>
                      <td className="px-4 py-3 font-bold text-green-700">{formatCurrency(p.net_salary)}</td>
                      <td className="px-4 py-3">
                        <span 
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            p.payment_status === 'Approved' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => setSelectedPayslip(p)}
                            className="text-[#2b3380] hover:text-[#2b3380] text-sm font-medium"
                          >
                            View Payslip
                          </button>
                          {(user.role === 'Admin' || user.role === 'HR Manager') && p.payment_status === 'Pending' && (
                            <button 
                              onClick={() => handleApprove(p.id)}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payslip Modal */}
        {selectedPayslip && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Payslip</h2>
                  <button 
                    onClick={() => setSelectedPayslip(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="border-b pb-4 mb-4">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold">Zobox Protech Limited</h3>
                    <p className="text-sm text-gray-600">Let's do it...</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Employee Name:</p>
                      <p className="font-semibold">{getEmployeeName(selectedPayslip.employee_id)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Employee ID:</p>
                      <p className="font-semibold">{selectedPayslip.employee_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Pay Period:</p>
                      <p className="font-semibold">{new Date(selectedPayslip.year, selectedPayslip.month - 1).toLocaleString('default', {month: 'long', year: 'numeric'})}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Payment Status:</p>
                      <p className={`font-semibold ${selectedPayslip.payment_status === 'Approved' ? 'text-green-600' : 'text-yellow-600'}`}>
                        {selectedPayslip.payment_status}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Attendance Summary</h4>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Working Days:</span>
                          <span className="font-medium">{selectedPayslip.working_days}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Present Days:</span>
                          <span className="font-medium">{selectedPayslip.present_days}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Absent Days:</span>
                          <span className="font-medium">{selectedPayslip.absent_days || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Paid Leaves:</span>
                          <span className="font-medium">{selectedPayslip.paid_leaves || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unpaid Leaves:</span>
                          <span className="font-medium">{selectedPayslip.unpaid_leaves || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Earnings</h4>
                    <div className="bg-gray-50 p-3 rounded space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Basic Salary:</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.basic_salary)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">HRA (House Rent Allowance):</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.hra || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">DA (Dearness Allowance):</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.da || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conveyance Allowance:</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.conveyance_allowance || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Medical Allowance:</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.medical_allowance || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Special Allowance:</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.special_allowance || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Other Allowances:</span>
                        <span className="font-medium">{formatCurrency(selectedPayslip.other_allowances || 0)}</span>
                      </div>
                      {(selectedPayslip.bonus > 0 || selectedPayslip.incentives > 0 || selectedPayslip.arrears > 0) && (
                        <div className="border-t pt-2">
                          {selectedPayslip.bonus > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Bonus:</span>
                              <span className="font-medium text-green-600">{formatCurrency(selectedPayslip.bonus)}</span>
                            </div>
                          )}
                          {selectedPayslip.incentives > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Incentives:</span>
                              <span className="font-medium text-green-600">{formatCurrency(selectedPayslip.incentives)}</span>
                            </div>
                          )}
                          {selectedPayslip.arrears > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Arrears:</span>
                              <span className="font-medium text-green-600">{formatCurrency(selectedPayslip.arrears)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">Gross Salary:</span>
                        <span className="font-semibold">{formatCurrency(selectedPayslip.gross_salary)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Deductions</h4>
                    <div className="bg-gray-50 p-3 rounded space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">PF - Employee:</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedPayslip.pf_employee || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ESI - Employee:</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedPayslip.esi_employee || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Professional Tax:</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedPayslip.professional_tax || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Income Tax (TDS):</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedPayslip.income_tax_tds || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Other Deductions:</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedPayslip.other_deductions || 0)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">Total Deductions:</span>
                        <span className="font-semibold text-red-600">{formatCurrency(selectedPayslip.total_deductions)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold" style={{color: '#2b3380'}}>Net Salary:</span>
                      <span className="text-2xl font-bold text-green-700">{formatCurrency(selectedPayslip.net_salary)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-center text-xs text-gray-500">
                  <p>This is a computer-generated payslip and does not require a signature.</p>
                  <p className="mt-1">Generated on: {formatDate(new Date())}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
export default Payroll;
